import { Divider, TextField } from '@mui/material';
import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';

import { AFFILIATE_CAMPAIGN_STATUS_OPTIONS, AFFILIATE_CAMPAIGN_TYPE_OPTIONS } from '../../../../constants';
import { AFFILIATE_CAMPAIGN_STATUS } from '../../../../constants/enums';
import { useMarketplace } from '../../../../hooks/useMarketplace';
import type { CampaignEditFormData } from '../../../../schema';
import FormSelect from '../../../molecules/FormSelect';
import ValidityPeriodSelect from '../../../molecules/ValidityPeriodSelect';

type CampaignFieldsProps = {
  children?: ReactNode;
  mode: 'create' | 'edit';
};

const styles = {
  landingPageField: {
    minWidth: { xs: '100%', md: 450 },
  },
};

const getCampaignStatusOptions = (mode: CampaignFieldsProps['mode']) => {
  if (mode === 'edit') return AFFILIATE_CAMPAIGN_STATUS_OPTIONS;

  return AFFILIATE_CAMPAIGN_STATUS_OPTIONS.map((option) => ({
    ...option,
    disabled: option.id !== AFFILIATE_CAMPAIGN_STATUS.READY,
  }));
};

const getLandingPageExamples = (marketplaceBaseUrl: string | null, isStandaloneCasino: boolean) => {
  if (!marketplaceBaseUrl) return null;

  return (
    <>
      {!isStandaloneCasino && `${marketplaceBaseUrl}/sports`}
      <br />
      {marketplaceBaseUrl}/casino
    </>
  );
};

const CampaignFields = ({ children, mode }: CampaignFieldsProps) => {
  const { config: marketplaceConfig, isStandaloneCasino } = useMarketplace();
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<CampaignEditFormData>();

  const isEditMode = mode === 'edit';
  const statusOptions = getCampaignStatusOptions(mode);

  const marketplaceBaseUrl = marketplaceConfig?.domain ? `https://${marketplaceConfig.domain}` : null;
  const landingPageExamples = getLandingPageExamples(marketplaceBaseUrl, isStandaloneCasino);
  const showLandingPageExamples = !isEditMode && !errors.url?.message;
  const landingPageHelperText = showLandingPageExamples ? landingPageExamples : errors.url?.message;

  return (
    <>
      <TextField error={!!errors.name} helperText={errors.name?.message} label='Name' {...register('name')} autoFocus />
      <FormSelect
        control={control}
        name='type'
        label='Type'
        options={AFFILIATE_CAMPAIGN_TYPE_OPTIONS}
        allowDeselect={false}
      />
      <TextField
        error={!!errors.code}
        helperText={errors.code?.message}
        label='Promo Code'
        {...register('code')}
        disabled={isEditMode}
        onChange={(event) => {
          setValue('code', event.target.value.toUpperCase(), { shouldDirty: true, shouldTouch: true });
        }}
      />

      {children}

      <TextField
        error={!!errors.url}
        helperText={landingPageHelperText}
        label='Landing page'
        {...register('url')}
        fullWidth
        sx={styles.landingPageField}
      />

      <Divider />
      <ValidityPeriodSelect control={control} disabled />
      <FormSelect control={control} name='status' label='Status' options={statusOptions} allowDeselect={false} />
      <Divider />

      <TextField
        error={!!errors.description}
        helperText={errors.description?.message}
        label='Description'
        {...register('description')}
        multiline
      />
    </>
  );
};

export default CampaignFields;
